/* Registrar release POR DESCOBERTA em PÁGINA CHEIA (ADR-0704). O staff escolhe canal + versão (dentre
   as presentes no bucket) e o admin LÊ os `latest*.yml` do build p/ auto-preencher
   plataforma/arch/sha512/tamanho — sem digitar path nem hash. Registra em lote (todos `draft`); a
   promoção segue manual (governança). Tela dedicada (não modal) com breadcrumb de volta à lista
   (ADR-0703) e os artefatos na primitive Table para o fluxo respirar. */
import type { DiscoveredArtifact, ReleaseChannel, StaffRole } from '@planuze/admin-contracts';
import {
  Breadcrumb,
  Button,
  EmptyState,
  InlineAlert,
  Input,
  Label,
  Page,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
} from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { PackageSearch } from 'lucide-react';
import { useEffect, useState } from 'react';
import { releaseErrorMessage } from '../components/releases/releases-error.js';
import { useReleaseRegisterContent } from '../content/sections/release-register.js';
import {
  useAvailableVersions,
  useDiscoverRelease,
  useRegisterDiscovered,
  useUpdateReleaseNotes,
} from '../hooks/use-releases.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

/* Gate espelha a lista (ADR-0659): só super_admin registra/promove/tira do ar. */
const MANAGE_ROLES: readonly StaffRole[] = ['super_admin'];

const KB = 1024;
const MB = KB * 1024;
const SHA_PREVIEW_LEN = 10;
const SEMVER_RE = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z-.]+)?$/;

function formatSize(bytes: number): string {
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / KB))} KB`;
}

/** Sugestão automática nunca ganha de texto humano, inclusive depois de uma nova descoberta. */
export function prefillReleaseNotes(
  current: string,
  suggested: string | null | undefined,
  touched: boolean,
): string {
  return touched ? current : (suggested ?? '');
}

interface ReleaseNotesDraft {
  readonly identity: string;
  readonly value: string;
  readonly touched: boolean;
}

/** Notas pertencem à seleção inteira; versão/canal novo nunca herda o texto da anterior. */
export function releaseNotesIdentity(channel: ReleaseChannel, version: string): string {
  return `${channel}:${version}`;
}

/**
 * Reconcilia uma resposta de discovery com o draft local.
 *
 * Na mesma identidade a edição humana vence qualquer resposta tardia/refetch. Ao trocar
 * `{channel,version}`, o draft anterior é descartado e a nova sugestão começa intocada.
 */
export function reconcileReleaseNotesDraft(
  current: ReleaseNotesDraft,
  identity: string,
  suggested: string | null | undefined,
): ReleaseNotesDraft {
  if (current.identity !== identity) {
    return { identity, value: suggested ?? '', touched: false };
  }
  const value = prefillReleaseNotes(current.value, suggested, current.touched);
  return value === current.value ? current : { ...current, value };
}

type ReleaseRegistrationAction = 'register-artifacts' | 'sync-release-notes' | 'none';

/** Um sidecar tardio pode reparar notas sem fabricar outro artefato nem alterar promoção. */
export function releaseRegistrationAction(
  pendingCount: number,
  artifactCount: number,
  releaseNotes: string,
  registeredReleaseNotes: string | null | undefined,
): ReleaseRegistrationAction {
  if (pendingCount > 0) return 'register-artifacts';
  if (
    artifactCount === 0 ||
    registeredReleaseNotes === undefined ||
    releaseNotes.trim() === '' ||
    releaseNotes === registeredReleaseNotes
  ) {
    return 'none';
  }
  return 'sync-release-notes';
}

export function ReleaseRegisterPage() {
  const content = useReleaseRegisterContent();
  const session = useStaffSession();
  const canManage = session !== null && MANAGE_ROLES.includes(session.role);
  const navigate = useNavigate();

  const [channel, setChannel] = useState<ReleaseChannel>('stable');
  const [version, setVersion] = useState('');
  const identity = releaseNotesIdentity(channel, version);
  const [releaseNotesDraft, setReleaseNotesDraft] = useState<ReleaseNotesDraft>({
    identity,
    value: '',
    touched: false,
  });

  const available = useAvailableVersions(channel);
  const discover = useDiscoverRelease(channel, version);
  const register = useRegisterDiscovered();
  const updateNotes = useUpdateReleaseNotes();

  const versionValid = SEMVER_RE.test(version);
  const artifacts = discover.data?.artifacts ?? [];
  const pending = artifacts.filter((a) => !a.alreadyRegistered);
  const busy = register.isPending || updateNotes.isPending;
  /* Query anterior pode continuar visível por um render durante a troca. Só uma resposta que ecoa
     a seleção atual pode preencher o draft novo. */
  const suggestedReleaseNotes =
    discover.data?.channel === channel && discover.data.version === version
      ? discover.data.suggestedReleaseNotes
      : undefined;
  const registeredReleaseNotes =
    discover.data?.channel === channel && discover.data.version === version
      ? discover.data.registeredReleaseNotes
      : undefined;
  /* Antes do effect de reset rodar, o render da nova identidade já enxerga texto vazio. Isso fecha
     a janela em que um clique rápido em Registrar enviaria as notas da versão anterior. */
  const releaseNotes = releaseNotesDraft.identity === identity ? releaseNotesDraft.value : '';
  const action = releaseRegistrationAction(
    pending.length,
    artifacts.length,
    releaseNotes,
    registeredReleaseNotes,
  );

  useEffect(() => {
    setReleaseNotesDraft((current) =>
      reconcileReleaseNotesDraft(current, identity, suggestedReleaseNotes),
    );
  }, [identity, suggestedReleaseNotes]);

  const goToList = (): void => {
    navigate({ to: '/releases' }).catch(() => undefined);
  };

  const onRegister = (): void => {
    if (action === 'sync-release-notes') {
      updateNotes.mutate(
        { channel, version, releaseNotes },
        {
          onSuccess: () => {
            toast.success(content.toastNotesSynced);
            goToList();
          },
          onError: (err) => toast.error(releaseErrorMessage(err)),
        },
      );
      return;
    }
    if (action !== 'register-artifacts') return;
    register.mutate(
      {
        channel,
        version,
        artifacts: pending.map((a) => ({
          platform: a.platform,
          arch: a.arch,
          /* `kind` vem da descoberta e é repassado: sem ele o instalador entraria como artefato de
             atualização e vazaria para o feed do electron-updater. */
          kind: a.kind,
          format: a.format,
          fileName: a.fileName,
          r2Key: a.r2Key,
          sha512: a.sha512,
          sizeBytes: a.sizeBytes,
        })),
        ...(releaseNotes.trim() !== '' && { releaseNotes }),
      },
      {
        onSuccess: (result) => {
          toast.success(
            result.skipped > 0
              ? content.toastRegisteredWithSkipped(result.registered, result.skipped)
              : content.toastRegisteredAllNew(result.registered),
          );
          goToList();
        },
        onError: (err) => toast.error(releaseErrorMessage(err)),
      },
    );
  };

  /* Trilha de volta para a lista (ADR-0703 §7.3): "Atualizações" navega, o item corrente é fixo. */
  const header = (
    <PageHeader>
      <PageTitleBlock>
        <Breadcrumb
          ariaLabel={content.breadcrumbAriaLabel}
          items={[
            { label: content.breadcrumbBack, onClick: goToList },
            { label: content.pageTitle },
          ]}
        />
        <PageTitle>{content.pageTitle}</PageTitle>
        <PageDescription>{content.pageDescription}</PageDescription>
      </PageTitleBlock>
    </PageHeader>
  );

  if (!canManage) {
    return (
      <Page>
        {header}
        <PageContent className="space-y-6">
          <InlineAlert variant="destructive">{content.noPermission}</InlineAlert>
        </PageContent>
      </Page>
    );
  }

  return (
    <Page>
      {header}
      <PageContent className="space-y-6">
        <section className="space-y-4 rounded-lg border border-border bg-card p-5">
          <h2 className="text-sm font-medium text-foreground">{content.originSectionTitle}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>{content.channelLabel}</Label>
              <Select
                value={channel}
                disabled={busy}
                onValueChange={(v) => {
                  setChannel(v as ReleaseChannel);
                  setVersion('');
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stable">stable</SelectItem>
                  <SelectItem value="beta">beta</SelectItem>
                  <SelectItem value="canary">canary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rel-version">{content.versionLabel}</Label>
              <Input
                id="rel-version"
                value={version}
                disabled={busy}
                onChange={(e) => setVersion(e.target.value.trim())}
                placeholder="1.3.0"
              />
            </div>
          </div>

          {available.data !== undefined && available.data.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                {content.versionsInBucketLabel}
              </span>
              <div className="flex flex-wrap gap-2">
                {available.data.map((v) => (
                  <button
                    key={v}
                    type="button"
                    disabled={busy}
                    onClick={() => setVersion(v)}
                    className={`rounded-md border px-2 py-1 font-mono text-xs transition-colors disabled:opacity-50 ${
                      v === version
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border text-muted-foreground hover:bg-accent/15 hover:text-foreground'
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              {content.noVersionsIntro}
              <code>
                {channel}/&lt;{content.noVersionsPathPlaceholder}&gt;/
              </code>
              {content.noVersionsOutro}
            </p>
          )}
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-medium text-foreground">{content.artifactsSectionTitle}</h2>
          {!versionValid && (
            <InlineAlert variant="info">
              {content.selectVersionHintIntro}
              <code>1.3.0</code>
              {content.selectVersionHintOutro}
            </InlineAlert>
          )}
          {versionValid && discover.isLoading && (
            <div className="grid place-items-center rounded-lg border border-border bg-card py-10">
              <Spinner />
            </div>
          )}
          {versionValid && discover.isError && (
            <InlineAlert variant="destructive">{releaseErrorMessage(discover.error)}</InlineAlert>
          )}
          {versionValid && discover.isSuccess && artifacts.length === 0 && (
            <EmptyState
              icon={<PackageSearch className="size-6" />}
              title={content.emptyArtifactsTitle}
              description={content.emptyArtifactsDescription}
            />
          )}
          {versionValid && discover.isSuccess && artifacts.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{content.table.platform}</TableHead>
                  <TableHead>{content.table.type}</TableHead>
                  <TableHead>{content.table.format}</TableHead>
                  <TableHead>{content.table.file}</TableHead>
                  <TableHead>{content.table.size}</TableHead>
                  <TableHead>{content.table.sha512}</TableHead>
                  <TableHead>{content.table.status}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* O formato identifica o arquivo: Linux pode ter `.deb` e `.rpm` com a mesma
                    plataforma, arch e tipo. */}
                {artifacts.map((a: DiscoveredArtifact) => (
                  <TableRow key={`${a.platform}:${a.arch}:${a.format}`}>
                    <TableCell className="font-medium text-foreground">
                      {a.platform}/{a.arch}
                    </TableCell>
                    <TableCell>
                      <StatusBadge variant={a.kind === 'installer' ? 'info' : 'neutral'}>
                        {a.kind === 'installer'
                          ? content.artifactKindInstaller
                          : content.artifactKindUpdate}
                      </StatusBadge>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {a.format === 'appimage' ? '.AppImage' : `.${a.format}`}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {a.fileName}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatSize(a.sizeBytes)}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {a.sha512.slice(0, SHA_PREVIEW_LEN)}…
                    </TableCell>
                    <TableCell>
                      {a.alreadyRegistered ? (
                        <StatusBadge variant="neutral">
                          {content.artifactAlreadyRegistered}
                        </StatusBadge>
                      ) : (
                        <StatusBadge variant="success">{content.artifactNew}</StatusBadge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>

        {versionValid && discover.isSuccess && artifacts.length > 0 && (
          <section className="space-y-1.5 rounded-lg border border-border bg-card p-5">
            <Label htmlFor="rel-notes">{content.releaseNotesLabel}</Label>
            <Textarea
              id="rel-notes"
              value={releaseNotes}
              disabled={busy}
              onChange={(e) => {
                setReleaseNotesDraft({ identity, value: e.target.value, touched: true });
              }}
              rows={5}
              placeholder={content.releaseNotesPlaceholder}
            />
            {action === 'sync-release-notes' && (
              <InlineAlert variant="info">{content.notesSyncHint}</InlineAlert>
            )}
          </section>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" onClick={goToList} disabled={busy}>
            {content.cancelButton}
          </Button>
          <Button onClick={onRegister} loading={busy} disabled={action === 'none'}>
            {action === 'register-artifacts'
              ? content.registerCount(pending.length)
              : action === 'sync-release-notes'
                ? content.syncNotesButton
                : content.nothingToRegister}
          </Button>
        </div>
      </PageContent>
    </Page>
  );
}
